/**
 * Indicaciones post operatorias que se entregan al paciente: implantes dentales, cirugía maxilofacial y cirugía bucal.
 * El contenido sale de las guías de cuidados de la clínica. El texto se arma aquí y se congela al firmar (el paciente recibe siempre lo que se emitió).
 *
 * Para agregar otra guía: 1) agregar la clave en TIPOS_INDICACION, 2) agregarla en GUIAS con sus apartados,
 * 3) agregarla en el CHECK de la tabla `indicaciones` con una migración nueva.
 */
import { fechaCertificado } from "./certificados.ts";

export const TIPOS_INDICACION = ["implantes", "maxilofacial", "cirugia_bucal"] as const;
export type TipoIndicacion = (typeof TIPOS_INDICACION)[number];

/** Apartado de una guía: `items` con «• » al inicio se dibujan como viñeta; sin ella, como párrafo. */
export interface Apartado { titulo: string; items: string[] }
export interface Guia { titulo: string; subtitulo: string; apartados: Apartado[]; /** cómo se llama la fecha en el encabezado */ etiquetaFecha: string }

const DIETA_REGLAS = [
  "• Temperatura y textura: coma solo cosas frías o a temperatura ambiente y muy suaves.",
  "• Evite el ají, las salsas picantes y los cítricos (limón, naranja): causan un ardor intenso y retrasan la cicatrización.",
  "• Está estrictamente prohibido comer granos, maní, nueces, semillas o arroz: estos pedacitos pueden meterse en el «hueco» de la herida, atrapar bacterias y causar una infección muy dolorosa.",
  "• No tome bebidas carbonatadas (gaseosas o agua con gas): las burbujas pueden aflojar y arrancar el coágulo curativo.",
  "• Evite las carnes que requieran masticar con fuerza, como el chicharrón, la fritada o el tostado, y los embutidos por su exceso de sal y condimentos irritantes.",
  "• Puede consumir lácteos (como helado de leche o yogur frío) durante las primeras 24 horas para ayudar a desinflamar, siempre que no se le haya indicado suspenderlos por la toma de ciertos antibióticos.",
];
const DIETA_INTRO = "Para que su herida sane rápido y sin infecciones, la dieta debe ser estricta.";

export const GUIAS: Record<TipoIndicacion, Guia> = {
  implantes: {
    titulo: "Guía de cuidados: implantes dentales", subtitulo: "Instrucciones para el cuidado de la osteointegración.", etiquetaFecha: "Fecha de la cirugía",
    apartados: [
      { titulo: "Proteja el implante", items: [
        "• No mastique de ese lado: mastique todos sus alimentos por el lado contrario. El tornillo no debe recibir presión mientras se adhiere al hueso.",
        "• Cuidado con las prótesis: si usa dentadura postiza removible, no se la ponga hasta que el doctor lo autorice.",
        "• No se toque la herida con la lengua ni con los dedos.",
      ] },
      { titulo: "Prohibición estricta de fumar", items: [
        "El tabaco es el enemigo número uno de los implantes. Fumar reduce el oxígeno en la sangre, retrasa la cicatrización y multiplica el riesgo de rechazo del implante.",
      ] },
      { titulo: "Alimentación y dieta", items: [DIETA_INTRO, ...DIETA_REGLAS] },
      { titulo: "Higiene", items: [
        "• Cepille sus otros dientes de forma normal.",
        "• En la zona del implante no pase el cepillo. Use un hisopo (cotonete) empapado en enjuague bucal y páselo con extrema delicadeza.",
        "• Si usa el enjuague de forma directa, hágalo moviendo suavemente la cabeza, recordando siempre no hacer buchadas ni escupir.",
        "• Tome el antibiótico completo hasta la última pastilla. Suspenderlo antes de tiempo puede causar una infección silenciosa en el hueso.",
      ] },
    ],
  },
  maxilofacial: {
    titulo: "Guía de cuidados: cirugía maxilofacial", subtitulo: "Instrucciones post operatorias para recuperación ósea y facial.", etiquetaFecha: "Fecha de la cirugía",
    apartados: [
      { titulo: "Control de la hinchazón y el dolor", items: [
        "• Hielo constante: durante las primeras 48 a 72 horas la hinchazón irá en aumento. Use compresas de hielo en el rostro la mayor cantidad de tiempo posible.",
        "• Duerma sentado: no duerma completamente acostado. Use 2 o 3 almohadas para mantener su cabeza por encima del nivel de su corazón.",
        "• Cero esfuerzo: no levante cosas pesadas, no haga ejercicio y evite agacharse durante al menos dos semanas.",
      ] },
      { titulo: "Alimentación y dieta", items: [
        "Su dieta debe ser estrictamente líquida o en papilla (sin grumos ni pedazos que requieran masticar). " + DIETA_INTRO,
        ...DIETA_REGLAS,
        "• Beba directamente de un vaso o con una cuchara, sin usar sorbetes.",
      ] },
      { titulo: "Higiene y señales de alerta", items: [
        "• Mantener la boca limpia es vital para no infectar los puntos. Use los enjuagues especiales dejando que el líquido bañe la zona, sin hacer buchadas y sin escupir.",
        "• Comuníquese de inmediato si presenta: fiebre alta (más de 38.5 °C), dificultad grave para respirar o tragar líquidos, o un sangrado abundante que no se detiene.",
      ] },
    ],
  },
  cirugia_bucal: {
    titulo: "Guía de cuidados: cirugía bucal", subtitulo: "Instrucciones para extracciones y cirugías menores.", etiquetaFecha: "Fecha de la cirugía",
    apartados: [
      { titulo: "Las primeras 24 horas", items: [
        "• Muerda la gasa: mantenga la gasa que le puso el doctor apretada firmemente durante 30 a 45 minutos. Si sigue sangrando un poco, cámbiela por una nueva y muerda otros 30 minutos.",
        "• No escupa ni use sorbetes: escupir, hacer «buches» o succionar crea presión en la boca que puede arrancar el coágulo de sangre. Trague la saliva acumulada o déjela caer suavemente en el lavabo.",
        "• Aplique frío: ponga una compresa de hielo envuelta en una toalla sobre su mejilla (15 minutos puesta, 15 minutos de descanso) para evitar que se hinche demasiado.",
      ] },
      { titulo: "Alimentación y dieta", items: [DIETA_INTRO, ...DIETA_REGLAS] },
      { titulo: "Higiene y medicación", items: [
        "• Día 1: no se cepille los dientes cerca de la herida ni use enjuagues bucales.",
        "• Día 2 en adelante: puede cepillarse suavemente, evitando tocar la herida. Si se le recetó un enjuague, úselo moviendo la cabeza suavemente, sin hacer buchadas, y deje caer el líquido sin escupir.",
        "• Tome las pastillas recetadas exactamente a la hora indicada, incluso si no siente dolor.",
      ] },
    ],
  },
};

export const nombreIndicacion = (t: TipoIndicacion) => GUIAS[t].titulo.replace(/^Guía de cuidados: /, "").replace(/^./, (c) => c.toUpperCase());
/** Cómo se nombra el documento en mensajes («indicaciones post operatorias de implantes dentales»). */
export const descripcionIndicacion = (t: TipoIndicacion) => `indicaciones post operatorias (${GUIAS[t].titulo.replace(/^Guía de cuidados: /, "")})`;

export interface DatosIndicacion {
  tipo: TipoIndicacion; fecha: string; procedimiento: string; adicionales: string; ciudad: string; contacto: string;
  paciente: { nombre: string; doc: string; hc: number };
}

export const MAX_ADICIONALES = 600;

export function problemasIndicacion(d: Pick<DatosIndicacion, "tipo" | "fecha" | "procedimiento" | "adicionales">, hoy: string): string[] {
  const e: string[] = [];
  if (!TIPOS_INDICACION.includes(d.tipo)) e.push("Elige el tipo de indicaciones.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.fecha)) e.push("Indica la fecha de la cirugía.");
  else if (d.fecha > hoy) e.push("La fecha de la cirugía no puede ser futura.");
  if (d.adicionales.length > MAX_ADICIONALES) e.push(`Las indicaciones adicionales admiten hasta ${MAX_ADICIONALES} caracteres.`);
  return e;
}

/**
 * Texto congelado. Bloques separados por una línea en blanco:
 * 1) lugar y fecha, 2) título, 3) subtítulo, 4) datos («Etiqueta: valor» por línea), 5) apartados («## Título» y luego viñetas o párrafos),
 * 6) indicaciones adicionales (opcional) y 7) contacto.
 */
export function textoIndicacion(d: DatosIndicacion): string {
  const g = GUIAS[d.tipo];
  const datos = [
    `Paciente: ${d.paciente.nombre}`,
    d.paciente.doc ? `C.I.: ${d.paciente.doc}` : "",
    `Historia clínica: ${d.paciente.hc}`,
    `${g.etiquetaFecha}: ${fechaCertificado(d.fecha)}`,
    d.procedimiento.trim() ? `Procedimiento: ${d.procedimiento.trim()}` : "",
  ].filter(Boolean).join("\n");
  return [
    `${d.ciudad ? `${d.ciudad}, ` : ""}${fechaCertificado(d.fecha)}`,
    "INDICACIONES POST OPERATORIAS",
    `${g.titulo.replace(/^Guía de cuidados: /, "").replace(/^./, (c) => c.toUpperCase())}\n${g.subtitulo}`,
    datos,
    ...g.apartados.map((a) => `## ${a.titulo}\n${a.items.join("\n")}`),
    d.adicionales.trim() ? `## Indicaciones adicionales de su profesional\n${d.adicionales.trim().replace(/\n{2,}/g, "\n")}` : "",
    `Si tiene dudas o nota algo que no le parece normal, comuníquese con la clínica${d.contacto ? `: ${d.contacto}` : ""}.`,
  ].filter(Boolean).join("\n\n");
}
